import { useId } from 'react';
import type { CSSProperties } from 'react';

import type { AspectoBotella } from '#components/mobile/budget/recuerdosAspecto';

import {
  bordeSello,
  contornoSello,
  letraSello,
  RADIO_TEXTO_SELLO,
} from './geometria';

type SelloProps = {
  aspecto: AspectoBotella;
  /** Texto en arco (`textoSello(nombre, fecha)`). */
  texto: string;
  /** Lado en px. Por defecto, 128. */
  tamano?: number;
  className?: string;
  style?: CSSProperties;
};

/**
 * Sello de la página de la bitácora: borde irregular de lacre, contorno
 * grabado por tipo de meta (círculo, cuadrado, octógono, rombo u óvalo),
 * tinta del color del lacre y el nombre con la fecha en un arco.
 */
export function Sello({
  aspecto: a,
  texto,
  tamano = 128,
  className,
  style,
}: SelloProps) {
  const id = `s${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const [tinta, brillo] = a.sello.tinta;
  const borde = bordeSello(a.semilla);
  const contorno = contornoSello(a.sello.forma);
  const letra = letraSello(texto);
  const r = RADIO_TEXTO_SELLO;
  return (
    <svg
      viewBox="0 0 128 128"
      width={tamano}
      height={tamano}
      className={className}
      style={style}
      data-testid="sello"
      data-forma={a.sello.forma}
      role="img"
      aria-label={texto}
    >
      <defs>
        <radialGradient id={`${id}g`} cx=".38" cy=".3" r=".85">
          <stop offset="0" stopColor={brillo} />
          <stop offset=".7" stopColor={tinta} />
          <stop offset="1" stopColor={tinta} />
        </radialGradient>
        <path
          id={`${id}a`}
          d={`M64 64m-${r} 0a${r} ${r} 0 1 1 ${2 * r} 0a${r} ${r} 0 1 1-${2 * r} 0`}
        />
      </defs>
      <path d={borde} fill={`url(#${id}g)`} />
      <path d={borde} fill="none" stroke="rgba(0,0,0,.22)" strokeWidth="1" />
      <path
        d={contorno}
        fill="none"
        stroke="rgba(0,0,0,.3)"
        strokeWidth="2.2"
      />
      <path
        d={contorno}
        fill="none"
        stroke="rgba(255,255,255,.22)"
        strokeWidth="1"
        transform="translate(-.8 -.8)"
      />
      <text
        fontFamily="Georgia,serif"
        fontSize={letra.tamano}
        letterSpacing={letra.espaciado}
        fill="rgba(0,0,0,.42)"
        aria-hidden
      >
        <textPath href={`#${id}a`} startOffset="50%" textAnchor="middle">
          {letra.texto}
        </textPath>
      </text>
      <g transform="translate(64 64)" fill="rgba(0,0,0,.34)">
        <path d="M0-17 3-3 17 0 3 3 0 17-3 3-17 0-3-3Z" />
        <path
          d="M0-9 1.5-1.5 9 0 1.5 1.5 0 9-1.5 1.5-9 0-1.5-1.5Z"
          fill="rgba(255,255,255,.25)"
          transform="rotate(45)"
        />
      </g>
    </svg>
  );
}
